/**
 * o16-gallery-lightbox.js
 * Full-screen lightbox for o16-shared-gallery sections.
 *
 * Behaviour:
 *  - Clicking any [data-o16-gallery-item] button opens the lightbox with the
 *    image referenced by data-full="<url>".
 *  - Prev / Next buttons cycle through ALL trigger items on the page.
 *  - Clicking the backdrop (outside the image) closes the lightbox.
 *  - Keyboard: Escape = close, ArrowLeft = prev, ArrowRight = next.
 *  - Body scroll is locked while the lightbox is open.
 *  - Fail-safe: if any part of setup throws, the error is caught and the
 *    buttons simply do nothing harmful.
 *
 * No external dependencies. Works alongside any CSS defined in the section's
 * {% stylesheet %} block — this file only manages state and DOM mutations.
 */

(function () {
  'use strict';

  /* ── Build the overlay DOM ── */
  var overlay = document.createElement('div');
  overlay.className = 'o16-gallery-lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Image viewer');

  var img = document.createElement('img');
  img.className = 'o16-gallery-lightbox__img';
  img.alt = '';

  /* Close button */
  var closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'o16-gallery-lightbox__close';
  closeBtn.setAttribute('aria-label', 'Close image viewer');
  closeBtn.innerHTML =
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M18 6L6 18M6 6l12 12" /></svg>';

  /* Prev button */
  var prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.className = 'o16-gallery-lightbox__prev';
  prevBtn.setAttribute('aria-label', 'Previous image');
  prevBtn.innerHTML =
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M15 18l-6-6 6-6" /></svg>';

  /* Next button */
  var nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.className = 'o16-gallery-lightbox__next';
  nextBtn.setAttribute('aria-label', 'Next image');
  nextBtn.innerHTML =
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M9 6l6 6-6 6" /></svg>';

  /* Frame: contains the image and the close button so the X sits at the
     image's top-right corner rather than the viewport's top-right corner.
     Prev/next remain on the overlay so they stay at the viewport edges. */
  var frame = document.createElement('div');
  frame.className = 'o16-gallery-lightbox__frame';
  frame.appendChild(img);
  frame.appendChild(closeBtn);

  overlay.appendChild(frame);
  overlay.appendChild(prevBtn);
  overlay.appendChild(nextBtn);
  document.body.appendChild(overlay);

  /* ── State ── */
  var triggers = [];
  var currentIndex = 0;
  var isOpen = false;
  var scrollY = 0;

  /* ── Helpers ── */
  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  function showImage(index) {
    /* Wrap around */
    var total = triggers.length;
    currentIndex = ((index % total) + total) % total;
    var trigger = triggers[currentIndex];
    var src = trigger.getAttribute('data-full');
    var alt = trigger.getAttribute('aria-label') || '';
    img.src = src;
    img.alt = alt;

    /* Hide prev/next if there is only one item */
    prevBtn.style.display = total > 1 ? '' : 'none';
    nextBtn.style.display = total > 1 ? '' : 'none';
  }

  function openAt(index) {
    if (isOpen) return;
    isOpen = true;
    showImage(index);
    lockScroll();
    overlay.classList.add('is-open');
    closeBtn.focus();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    overlay.classList.remove('is-open');
    unlockScroll();
    /* Return focus to the trigger that opened the lightbox */
    var trigger = triggers[currentIndex];
    if (trigger) trigger.focus();
  }

  /* ── Event wiring ── */

  /* Backdrop click — close if the click target is the overlay itself */
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) close();
  });

  closeBtn.addEventListener('click', close);

  prevBtn.addEventListener('click', function (e) {
    e.stopPropagation();
    showImage(currentIndex - 1);
  });

  nextBtn.addEventListener('click', function (e) {
    e.stopPropagation();
    showImage(currentIndex + 1);
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        showImage(currentIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        showImage(currentIndex + 1);
        break;
    }
  });

  /* ── Collect triggers and bind open handlers ── */
  /* Use MutationObserver so sections loaded after initial render (e.g. in the
     Theme Editor) are also wired correctly. */
  function wireTriggers() {
    var items = document.querySelectorAll('[data-o16-gallery-item]');
    triggers = Array.prototype.slice.call(items);
    triggers.forEach(function (btn, index) {
      /* Guard against double-binding */
      if (btn.getAttribute('data-gallery-wired')) return;
      btn.setAttribute('data-gallery-wired', '1');
      btn.addEventListener('click', function () {
        openAt(index);
      });
    });
  }

  /* Wire on DOMContentLoaded (script is deferred so DOM is already ready,
     but be safe) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireTriggers);
  } else {
    wireTriggers();
  }

  /* Re-wire when the Theme Editor adds/modifies sections */
  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(function () {
      wireTriggers();
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
